import { useContext, type FC } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import {
  canViewMhaStatusDashboard,
  canViewSupersetDashboards,
} from "@thalamos/aspire-common";

import { NotADashboardViewer } from "../supersetDashboardPage/SupersetDashboardPage.js";
import { buildDashboardItems } from "./buildDashboardItems.js";
import { DashboardChooser } from "./DashboardChooser.js";
import { apiHooks } from "~/api.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { routeFns } from "~/routePaths.js";

export const DashboardLandingPage: FC = () => {
  const userContext = useContext(LoggedInUserContext);
  const user = userContext?.user;
  const navigate = useNavigate();

  const isViewer = canViewSupersetDashboards(user);
  const showMha = canViewMhaStatusDashboard(user);

  const embedDashboards =
    user?.sessionOrganisationConfiguration?.embedDashboards ?? [];

  const onlyEmbedDashboard =
    embedDashboards.length === 1 ? embedDashboards[0] : undefined;
  const redirectToSingle = !showMha && onlyEmbedDashboard !== undefined;

  const shouldFetchDashboardNames =
    !!isViewer && !redirectToSingle && embedDashboards.length > 0;
  const organisationId = shouldFetchDashboardNames
    ? (user?.sessionContext?.organisationId ?? null)
    : null;

  const [{ data: dashboards, loading }] =
    apiHooks.reports.supersetDashboards(organisationId);

  const mhaOrganisationId = showMha
    ? (user?.sessionContext?.organisationId ?? null)
    : null;

  const [{ data: mhaLastViewed }] =
    apiHooks.reports.mhaDashboardLastViewed(mhaOrganisationId);

  if (!isViewer) {
    return <NotADashboardViewer />;
  }

  if (!showMha && onlyEmbedDashboard !== undefined) {
    return (
      <Navigate
        to={routeFns.dashboardViewer(onlyEmbedDashboard.supersetDashboardId)}
        replace
      />
    );
  }

  if (loading) {
    return <DashboardChooser type="loading" />;
  }

  const section136Enabled =
    user?.sessionOrganisationConfiguration?.patientStateEnabled ?? false;

  const items = buildDashboardItems(
    {
      showMha,
      section136Enabled,
      dashboards: dashboards ?? [],
      mhaLastViewedAt: mhaLastViewed?.lastViewedAt ?? null,
    },
    navigate,
  );

  return <DashboardChooser type="loaded" items={items} />;
};
